import { ArrowPathRoundedSquareIcon } from "@heroicons/react/24/outline";
import api from "@src/api/api";
import Panel from "@src/components/box/Panel";
import LinkPreview from "@src/components/elements/LinkPreview";
import UserWidget from "@src/components/elements/UserWidget";
import PostCommentSection from "@src/components/elements/posts/comments/PostCommentSection";
import Lightbox from "@src/components/elements/shared/Lightbox";
import TextView from "@src/components/elements/textedit/TextView";
import { useVisibility } from "@src/hooks/useVisibility";
import PostsActionBar from "@src/pages/news/posts/PostsActionBar";
import PostsDropdown from "@src/pages/news/posts/PostsDropdown";
import { useAppSelector } from "@src/state/hooks";
import getName from "@src/utils/getName";
import { Post } from "@src/utils/post";
import { User } from "@src/utils/user";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactPlayer from "react-player";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

type Props = {
  postId: string;
  post: Post;
  author: User;
  text: string;
  created_at: string;
  key?: string;
  repost?: Post;
  onSwitch?: (selectedPost: string) => void;
  lightboxSelection?: string;
  next?: string;
  prev?: string;
};

const checkHideLinkText = (lexicalObjectRaw: string) => {
  try {
    const lexicalObject = JSON.parse(lexicalObjectRaw);
    if (lexicalObject["root"]["children"].length !== 1) return false;
    if (lexicalObject["root"]["children"][0]["children"].length !== 1) return false;
    return (
      lexicalObject["root"]["children"][0]["children"][0]["type"] === "link" ||
      lexicalObject["root"]["children"][0]["children"][0]["type"] === "autolink"
    );
  } catch (e) {
    return false;
  }
};

const removeLinkFromLexicalObject = (lexicalObjectRaw: string, link: string) => {
  try {
    if (!link) return lexicalObjectRaw;

    const lexicalObject = JSON.parse(lexicalObjectRaw);
    if (lexicalObject["root"]["children"].length === 0) return lexicalObjectRaw;
    if (lexicalObject["root"]["children"][0]["children"].length === 0) return lexicalObjectRaw;

    for (let i = 0; i < lexicalObject["root"]["children"].length; i++) {
      if (lexicalObject["root"]["children"][i]["children"].length === 0) continue;
      if (
        lexicalObject["root"]["children"][i]["children"][0]["type"] === "link" ||
        lexicalObject["root"]["children"][i]["children"][0]["type"] === "autolink"
      ) {
        if (lexicalObject["root"]["children"][i]["children"][0]["url"] === link) {
          lexicalObject["root"]["children"][i]["children"].shift();
          break;
        }
      }
    }
    return JSON.stringify(lexicalObject);
  } catch (e) {
    return lexicalObjectRaw;
  }
};

export function formatDateDifference(currentDate: Date, postDate: Date): string {
  const diffInSeconds = Math.floor((currentDate.getTime() - postDate.getTime()) / 1000);
  const mins = Math.floor(diffInSeconds / 60);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);

  if (mins < 1) return "Gerade eben";
  if (mins === 1) return "Vor einer 1 Min";
  if (mins < 60) return `vor ${mins} Min`;
  if (hours === 1) return "Vor 1 h";
  if (hours < 24) return `vor ${hours} h`;
  if (days === 1) return "Vor 1 T";
  if (days < 7) return `vor ${days} T`;
  if (weeks === 1) return "Vor 1 W";
  return `vor ${weeks} W`;
}

const PostWidget = ({ author, created_at, ...props }: Props) => {
  const [openComments, setOpenComments] = useState(false);
  const [playingVideo, setPlayingVideo] = useState<string | null>(null);
  const [downloadRunning, setDownloadRunning] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const urlRegex = /(https?:\/\/[^\s]+)|#([a-zA-ZäöüÄÖÜß]+)/g;
  const queryClient = useQueryClient();
  const { user } = useAppSelector(state => state.user);
  const [isVisible, ref] = useVisibility();
  const [viewed, setViewed] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const containerRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const videoRefs = useRef<{ [key: string]: ReactPlayer | null }>({});

  const linkPreview = props.repost ? props.repost.link_preview : props.post.link_preview;
  const text = removeLinkFromLexicalObject(
    props.repost ? props.repost.text : props.text,
    props.post.link,
  );
  const media = props.repost ? props.repost.media : props.post.media;
  const watch = props.repost ? props.repost.watch : props.post.watch;

  const hideLinkText = linkPreview && (linkPreview.url === text || checkHideLinkText(text));

  let match;
  const links: string[] = [];
  while ((match = urlRegex.exec(text)) !== null) {
    links.push(match[0]);
  }

  const like = useMutation([`like-${props.post.id}-${user?.id}`], {
    mutationFn: async () => {
      const res = await api.get(`/api/v1/posts/${props.post.id}/like`);
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["posts"] });
    },
    onError: () => {
      alert("Failed");
    },
  });

  const unlike = useMutation([`unlike-${props.post.id}-${user?.id}`], {
    mutationFn: async () => {
      const res = await api.get(`/api/v1/posts/${props.post.id}/unlike`);
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["posts"] });
    },
    onError: () => {
      alert("Failed");
    },
  });

  useEffect(() => {
    if (isVisible && !viewed) {
      setViewed(true);
      axios.get(`/api/v1/posts/${props.post.id}/view`);
    }
  }, [isVisible, props.post.id, viewed]);

  useEffect(() => {
    const currentContainerRefs = { ...containerRefs.current };

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const videoId = entry.target.getAttribute("data-video-id");
            setPlayingVideo(videoId);
          } else {
            const videoId = entry.target.getAttribute("data-video-id");
            if (playingVideo === videoId) {
              setPlayingVideo(null);
            }
          }
        });
      },
      {
        threshold: 0.5, // Trigger if 80% of the video container is visible
      },
    );
    Object.values(currentContainerRefs).forEach(containerRef => {
      if (containerRef) {
        observer.observe(containerRef);
      }
    });
    return () => {
      Object.values(currentContainerRefs).forEach(containerRef => {
        if (containerRef) {
          observer.unobserve(containerRef);
        }
      });
    };
  }, [playingVideo]);

  return (
    <Panel mobileBorder={false}>
      <div ref={ref}>
        <div className="flex w-full gap-3">
          <div className="w-[35px]">
            <UserWidget user={author} size={32} follow={false} online={false} />
          </div>
          <div className="flex-1">
            <div className="flex flex-col">
              <div className="flex flex-row items-center justify-between">
                <div className="flex flex-1 items-center gap-3">
                  <NavLink
                    to={`/profile/${author.id}`}
                    state={{
                      backlink: location.state?.backlink ?? location.pathname,
                    }}
                    className="flex flex-row  gap-1 truncate"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                        {(getName(author) || "").length > 30
                          ? `${(getName(author) || "").substring(0, 30)}...`
                          : getName(author)}
                      </p>
                      <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                        @
                        {(author.normalised_account_name || "").length > 20
                          ? `${(author.normalised_account_name || "").substring(0, 20)}...`
                          : author.normalised_account_name}
                      </p>
                    </div>
                    {author.type === "person" && (
                      <img src="/images/person_verify.svg" className="mt-1 size-4 shrink-0" />
                    )}
                    {author.type === "company" && (
                      <img src="/images/company_verify.svg" className="mt-1 size-4 shrink-0" />
                    )}
                    {author.type === "club" && (
                      <img src="/images/club_verify.svg" className="mt-1 size-4 shrink-0" />
                    )}
                  </NavLink>
                  <div
                    className="ml-auto shrink-0"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <PostsDropdown
                      post={props.post}
                      onDownloadStart={() => setDownloadRunning(true)}
                      onDownloadEnd={() => setDownloadRunning(false)}
                      onDownloadProgress={progress => setDownloadProgress(progress)}
                    />
                  </div>
                </div>
                {props.repost && (
                  <NavLink
                    to={`/profile/${props.repost.author.id}`}
                    state={{
                      backlink: location.state?.backlink ?? location.pathname,
                    }}
                    className="flex flex-row items-center gap-1 truncate"
                  >
                    <ArrowPathRoundedSquareIcon className="size-4 shrink-0" />
                    <div className="whitespace-nowrap text-xs font-medium">
                      {t("components.posts.repostedBy")}
                    </div>
                    <UserWidget
                      user={props.repost.author}
                      size={16}
                      follow={false}
                      online={false}
                    />
                    <span className="text-xs font-medium">{getName(props.repost.author)}</span>
                  </NavLink>
                )}
              </div>
              <div className="text-[0.7rem] leading-4 text-gray-600">
                {formatDateDifference(new Date(), new Date(created_at))}
              </div>
            </div>
            <div
              style={{
                maxWidth: ref.current?.offsetWidth ? ref.current?.offsetWidth - 45 : "100%",
              }}
              className="break-words"
            >
              {!!(props.lightboxSelection === props.post.id) && (
                <Lightbox
                  onClose={() => {
                    props.onSwitch && props.onSwitch("");
                  }}
                  onSwitch={(id: string) => props.onSwitch && props.onSwitch(id)}
                  post={props.post}
                  prev={props.prev}
                  next={props.next}
                />
              )}
              {downloadRunning && (
                <Panel>
                  {t("components.posts.downloading")}
                  <div className="h-2.5 w-full rounded-full">
                    <div
                      className="h-2.5 rounded-full bg-darkblue"
                      style={{ width: `${downloadProgress}%` }}
                    ></div>
                  </div>
                </Panel>
              )}
              {linkPreview && <LinkPreview link={linkPreview} />}
              {watch && media && media[0] && (
                <div
                  className="mt-3 aspect-square overflow-hidden rounded-2xl border border-gray-400"
                  ref={el => (containerRefs.current["video-1"] = el)}
                  data-video-id="video-1"
                >
                  <ReactPlayer
                    width="100%"
                    controls={true}
                    playing={playingVideo === "video-1"}
                    height="100%"
                    url={`${media[0].data_url}/playlist.m3u8`}
                    ref={el => (videoRefs.current["video-1"] = el)}
                  />
                </div>
              )}
              {media.length > 0 && (
                <div
                  className="mt-3 aspect-square cursor-pointer overflow-hidden rounded-2xl border border-gray-400"
                  onClick={() => {
                    if (media[0].type === "video") {
                      navigate(`/watch/für_dich?featured=${media[0].id}`);
                    } else {
                      props.onSwitch && props.onSwitch(props.postId);
                    }
                  }}
                  ref={el => (containerRefs.current["video-2"] = el)}
                  data-video-id="video-2"
                >
                  <ReactPlayer
                    width="100%"
                    height="100%"
                    className="react-player"
                    url={`${media[0].data_url}/playlist.m3u8`}
                    playing={playingVideo === "video-2"}
                    controls={false}
                    muted={true}
                    ref={el => (videoRefs.current["video-2"] = el)}
                  />
                </div>
              )}
              {!hideLinkText && text && <TextView value={text} />}
            </div>
            <PostsActionBar
              toggleComments={() => setOpenComments(value => !value)}
              toggleLike={() => {
                if (user) {
                  if (props.post.liked) {
                    unlike.mutate();
                  } else {
                    like.mutate();
                  }
                }
              }}
              isLiked={props.post.liked}
              isReposted={props.post.is_reposted}
              likeCount={props.post.like_count}
              viewCount={props.post.view_count}
              bookmarkCount={props.post.bookmark_count}
              repostCount={props.post.repost_count}
              bookmarked={props.post.bookmarked}
              postId={props.post.id}
              commentCount={props.post.comment_count}
            />
          </div>
        </div>
      </div>
      {openComments && (
        <PostCommentSection postId={props.postId} viewCount={props.post.view_count} />
      )}
    </Panel>
  );
};

export default PostWidget;
